import { Injectable } from '@angular/core';
import { Order, PartyRoleCode } from '../../orders/place-order/place-order.model';
import _ from 'lodash';
import { Attachment, Contact, ContactTitle, OrderInfo } from '../../order-info/order-info.model';
import { Authorization, Authorizations, RecordSubjectName, State } from '../../orders/authorization/authorization.model';
import { Subpoena, PlaintiffCounsel, DefendantCounsel, Plaintiff, Defendant } from '../../orders/subpoena/subpoena.model';
import { Subject, Subjects } from '../../orders/subjects/subjects.model';
import { DiagnosticImage, Record, Records, SocialSecurityRes, VARes } from '../../orders/records/records.model';
import { DatePipe } from '@angular/common';
import { DateUtilService } from '../date-util/date-util.service';

@Injectable({
  providedIn: 'root'
})
export class OrderMapperService {

  constructor(private datePipe: DatePipe, private dateUtilService: DateUtilService) { }

  vocabData: any;

  formatDate(date?: string): string {
    // return this.datePipe.transform(date, 'MM/dd/yyyy', 'UTC') || '';

    if (!date) return '';

    let parsedDate: Date;

    // Check if date includes time or timezone info
    const hasTimeOrZone = /T|\d{2}:\d{2}/.test(date);

    if (!hasTimeOrZone) {
      parsedDate = new Date(date + 'T00:00:00Z');
    } else {
      parsedDate = new Date(date);
    }

    return this.datePipe.transform(parsedDate, 'MM/dd/yyyy') || '';
  }

  getDocTitleDescription(title: string = '', vocabData: any) {
    const vocabTitleData = vocabData?.find((d: any) => d.vocabularyName === 'OrderDocumentCode');
    const selectedTitle = vocabTitleData?.vocabularyItems?.find((item: any) => item?.code === title);
    return selectedTitle?.description;
  }

  getTitleDescription(title: string = '', vocabData: any) {
    const vocabTitleData = vocabData?.find((d: any) => d.vocabularyName === 'ContactTitle');
    const selectedTitle = vocabTitleData?.vocabularyItems?.find((item: any) => item?.code === title);
    return selectedTitle?.description;
  }

  getImageTypeCode(description: string = '', vocabData: any) {
    const vocabTitleData = vocabData?.find((d: any) => d.vocabularyName === 'ImagingTypes');
    const selectedType = vocabTitleData?.vocabularyItems?.find((item: any) => item?.description === _.trim(description));
    return selectedType?.code;
  }

  getStateDescription(stateCode: string = '', vocabData: any) {
    const vocabCityData = vocabData?.find((d: any) => d.vocabularyName === 'PhysicalLocationUsAddressStateCode');
    const selectedCity = vocabCityData?.vocabularyItems?.find((item: any) => item?.code === stateCode);
    return selectedCity?.description;
  }

  public mapOrderJsonToFormObject(order: any, data: any, vocabData: any): Order {
    let result: Order = {
      orderInfo: {} as OrderInfo,
      subjects: {
        subjects: [],
      } as Subjects,
      records: {
        records: [],
      } as Records,
    };

    const caseData = data.case;
    const retrievalData = data.retrievalObjective;
    const attachmentsData = data.attachments;
    const orderType = retrievalData?.orderTypeCode;
    this.vocabData = vocabData;
    if (order && result.orderInfo) {
      result.orderInfo.orderInfoAccountName = order?.orderedByAccountId;
      result.orderInfo.orderInfoOrderOwner = order?.orderedBy;
      result.orderInfo.orderInfoClientName = order?.customerId;
    }

    if (orderType === 'subpoena-to-be-prepared') {
      result.subpoena = {} as Subpoena;
    }
    if (orderType === 'authorization-not-provided') {
      result.authorization = {} as Authorizations;
    }

    this.mapCaseDetails(caseData, orderType, result);
    this.mapRetrievalDetails(retrievalData, orderType, result);
    this.mapAttachments(attachmentsData, result);

    const sortedRecords = [...(result.records?.records ?? [])].sort((a, b) => {
      const toSegments = (num: string) =>
        (num || '0').split('-').map((s) => parseInt(s, 10) || 0);
      const segsA = toSegments(a.requestNumbers?.[0] ?? '0');
      const segsB = toSegments(b.requestNumbers?.[0] ?? '0');
      for (let i = 0; i < Math.max(segsA.length, segsB.length); i++) {
        const diff = (segsA[i] ?? 0) - (segsB[i] ?? 0);
        if (diff !== 0) return diff;
      }
      return 0;
    });
    if (sortedRecords && result.records) {
      result.records.records = sortedRecords;
    }
    return result;
  }

  mapCaseDetails(caseData: any, orderType: string, result: Order) {
    if(result.orderInfo) {
      result.orderInfo.orderInfoRefNum = caseData.referenceNumber;
      result.orderInfo.orderInfoDate = this.formatDate(caseData.incidentDate);
      // result.orderInfo.orderInfoTenantName = caseData.firmCode ;
      result.orderInfo.caseMatter = (caseData as any)?.qualifiers?.caseMatter || '';
    }
    if (result.subpoena) {
      result.subpoena.caseName = caseData?.caseName || '';
      result.subpoena.caseNumber = caseData?.caseNumber || '';
    }

    let defAttKey : any;
    let partyFlag: Boolean = false;
    _.forEach(caseData?.parties, item => {
      if(item.roles?.find((role: any) => role.roleCode === PartyRoleCode.DefendantAttorney)){
        if (item.party) {
          item.party.keys = item.party.keys || [];
          partyFlag = this.isExternalIdTrue(partyFlag, item.party.keys);
          if(partyFlag === true){
            defAttKey = item.party.keys.find((key :any )=> key.typeCode === "external-id")?.components?.["external-id"];
          }
          partyFlag = false;
        }
      }
      
  });

  let defendants: Defendant[] = [];
    caseData?.partyRelationships.forEach((relation: any) => {
      if (
        defAttKey === relation.sourcePartyKey
      ) {
        
        caseData?.parties.forEach((party: any) => {
          if (party?.party?.keys?.find((item: any) => item?.typeCode === 'external-id')?.components['external-id'] === relation?.targetPartyKey) {
            let defendant: Defendant = {
              name: party?.party?.displayName,
              keys: party?.party?.keys,
              streetAddress: '',
              city: '',
              state: { name: '', value: '' },
              value: party?.party?.displayName,
              zip: ''
            };
            defendants.push(defendant);
          }
        
      });
    }
    });
    
    const counselInfo: DefendantCounsel = {
        defendants: defendants,
        defendantCounselAttorneyName: "",
        defendantCounselFirmName: result?.orderInfo?.orderInfoAccountName || "",
        streetAddress: "",
        city: "",
        state: {name: "", value: ""},
        zip: "",
        phone: "",
        extn: "",
        fax: "",
        email: "",
        keys: [],
        defendantCounselBarNumer: ""
      };
    if (result.subpoena && !result.subpoena?.defendantCounseals)
      result.subpoena.defendantCounseals = [];
    result.subpoena?.defendantCounseals.push(counselInfo);
    caseData?.parties.forEach((element: any) => {
      const distinctRoleCodes = _.uniq(_.map(element.roles, 'roleCode'));
        switch (distinctRoleCodes[0]) {
          case 'defendant-paralegal': //contacts
          case 'contact-other':
          case 'defendant-attorney':
            this.mapContacts(element, result);
            break;
          case PartyRoleCode.PlaintiffAttorney: // authorization/subpoena
            this.mapPlaintiffCounsel(element, orderType, result, caseData);
            break;
          case PartyRoleCode.CoDefendantAttorney: //subpoena defendants
            this.mapDefendantCounsel(element, orderType, result, caseData);
            break;
          case PartyRoleCode.Court: //subpoena court and judge
          this.mapCourt(element, result, caseData);
            break;
        }
      });
  }

  mapRetrievalDetails(retrievalData: any, orderType: string,  result: Order) {
    if(result.orderInfo) {
      result.orderInfo.orderInfoSpecialInstructions = retrievalData.specialInstructions;
      result.orderInfo.selectedCategory = retrievalData.orderTypeCode;
      result.orderInfo.selectedLevel = retrievalData.serviceLevelCode === 'Rush' ? true : false;
    }
    
    let medicalProviderParties: any[] = [];

    retrievalData?.parties.forEach((element: any) => {
      const distinctRoleCodes = _.uniq(_.map(element.roles, 'roleCode'));
        switch (distinctRoleCodes[0]) {
          case PartyRoleCode.BillTo:
            if(result.orderInfo) {
              let emailLocator = _.find(element.party.locators, ['typeCode', 'email']);
              result.orderInfo.orderInfoBillTo = element.party.displayName;
              result.orderInfo.orderInfoBillToEmail = emailLocator?.email?.address;
              result.orderInfo.orderInfoBillingToKeys = element.party.keys;
            }
            break;
          case PartyRoleCode.Adjuster:
            if(result.orderInfo) {
              let emailLocator = _.find(element.party.locators, ['typeCode', 'email']);
              result.orderInfo.orderInfoAdjusterName = element.party.displayName;
              result.orderInfo.orderInfoAdjusterEmail = emailLocator?.email?.address;
              result.orderInfo.orderInfoAdjusterKeys = element.party.keys;
            }
            break;
          case PartyRoleCode.DeliverTo:
            this.mapDelivary(element, result);            
            break;
          case PartyRoleCode.RecordSubject:
            this.mapRecordSubject(element, result);
            break;
          case PartyRoleCode.RecordsCustodian:
            this.mapRecords(element,retrievalData, result);
            break;
          case PartyRoleCode.MedicalProvider:
            // this.mapMedicalProvider(element, result);
            medicalProviderParties.push(element);
            break;
          case PartyRoleCode.AmbulanceService:
          
            break;
        }
      });

    medicalProviderParties?.forEach((element: any) => {
      const distinctRoleCodes = _.uniq(_.map(element.roles, 'roleCode'));
        switch (distinctRoleCodes[0]) {
          case PartyRoleCode.MedicalProvider:
            this.mapMedicalProvider(element, result);
            break;
        }
      });

    // update record subjects 
    result.records?.records?.forEach((element: any) => {
      if(element?.requestNumbers && element?.requestNumbers.length > 0 ){
        const recordSubject = result.subjects?.subjects.find((subject: any) => subject.requestNumbers?.includes(element?.requestNumbers[0]))?.recordSubjectName ?? '';
        element.subject = recordSubject;
      }
        
    });
  }

  mapRecords(element: any, retrievalData: any, result: Order) {
    let providerInfo: Record = {} as Record;
    let reqNumber: string;
    let provider: string = element.party.displayName;
    let streetAddress: string = '';
    let aptSuiteOther: string = '';
    let city: string = '';
    let state: State = {name: '', value: ''};
    let zip: string = '';
    let email: string = '';
    let phone: string = '';
    let extn: number;
    let fax: string = '';
    let recordType: string[] = [];
    let recordSubject: string = '';
    let doctor: string = '';
    let subpoenaLanguage: string = '';
    let recordRange: string[] = [];
    let notes: string = '';
    let ambulanceDateOfService: string = '';
    let ambulanceTimeOfService: string = '';
    // let ambulancePickupAddress: string = '';
    let ambulancePickupStreetAddress: string = '';
    let ambulancePickupCity: string = '';
    let ambulancePickupState: State = {name: '', value: ''};
    let ambulancePickupZip: string = '';
    // let ambulanceDropAddress: string = '';
    let ambulanceDropStreetAddress: string = '';
    let ambulanceDropCity: string = '';
    let ambulanceDropState: State = {name: '', value: ''};
    let ambulanceDropZip: string = '';
    let militaryBranch: string = '';
    let militaryServiceNumber: string = '';
    let va: VARes = {} as VARes;
    let unionMembershipNumber: string = '';
    let otherDescriptionOfRecords: string = '';
    let diagnosticTypeCodes: DiagnosticImage[] = [];
    let diagnosticComments: string = '';
    let diagnosticFocusArea: string = '';
    let insuranceClaimNumber: string = '';
    let insurancePolicyNumber: string = '';
    let employmentSSN: string = '';
    let irsSSN: string = '';
    let socialSecuritySSN: string = '';
    let ssnAddress: string = '';
    let requestNumbers: string[] =[];
    let dateToServiceFrom: string;
    let dateToServiceto: string;
    let socialSecurityRes: SocialSecurityRes = {} as SocialSecurityRes;

    element.roles.forEach((role: any) => {
      if (role.roleCode === 'records-custodian' && role.requestNumbers) {
        requestNumbers = role.requestNumbers;
        role.requestNumbers.forEach((requestNumber: any) => {
          element.party.locators.forEach((locator: any) => {
            if (locator.typeCode === 'email') {
              email = locator.email?.address;
            }

            if (locator.typeCode === 'phone' && locator.phone.purpose === 'work') {
              phone = locator.phone?.number;
              extn = locator.phone?.extension;
            }

            if (locator.typeCode === 'phone' && locator.phone.purpose === 'fax') {
              fax = locator.phone?.number;
            }

            if (locator.typeCode === 'location') {
              streetAddress = locator?.location?.location?.usAddress?.lineOne;
              aptSuiteOther = locator?.location?.location?.usAddress?.lineTwo;
              city = locator?.location?.location?.usAddress?.city ;
              state.value = locator?.location?.location?.usAddress?.stateCode ;
              state.name = this.getStateDescription(state.value, this.vocabData);
              zip = locator?.location?.location?.usAddress?.postalCode;
            }
          });

          // recordSubject = result.subjects?.subjects.find((subject: any) => subject.requestNumbers?.includes(requestNumber))?.recordSubjectName ?? '';

          retrievalData.requests.forEach((request: any) => {
            if (request.requestNumber === requestNumber) {
              reqNumber = requestNumber
              recordType = _.map(request.scope, 'recordTypeCode');
              
              request.scope.forEach((scope: any) => {
                // recordRange = (scope.beginDate && scope.endDate) ? [] : ['anyorall'];
                if(scope.beginDate && scope.endDate) {
                  dateToServiceFrom = this.formatDate(scope.beginDate);
                  dateToServiceto = this.formatDate(scope.endDate);
                  recordRange = []
                } else {
                  recordRange = ['anyorall'];
                }


                if (scope.recordTypeCode === 'ambulance') {
                  ambulanceDateOfService = this.formatDate(scope.qualifiers.ambulanceDateOfService);
                  ambulanceTimeOfService = scope.qualifiers.ambulanceTimeOfService ? scope.qualifiers.ambulanceTimeOfService : '';
                  ambulancePickupStreetAddress = scope.qualifiers.ambulancePickupLine1 ;
                  ambulancePickupCity =     scope.qualifiers.ambulancePickupCity ;
                  // ambulancePickupState =     scope.qualifiers.ambulancePickupState;
                  ambulancePickupState.value = scope.qualifiers.ambulancePickupState ;
                  ambulancePickupState.name = this.getStateDescription(ambulancePickupState.value, this.vocabData);
                  ambulancePickupZip =     scope.qualifiers.ambulancePickupPostal ;

                  ambulanceDropStreetAddress = scope.qualifiers.ambulanceDropOffLine1 ;
                  ambulanceDropCity =     scope.qualifiers.ambulanceDropOffCity ;
                  // ambulanceDropState =     scope.qualifiers.ambulanceDropOffState;
                  ambulanceDropState.value = scope.qualifiers.ambulanceDropOffState ;
                  ambulanceDropState.name = this.getStateDescription(ambulanceDropState.value, this.vocabData);
                  ambulanceDropZip =     scope.qualifiers.ambulanceDropOffPostal ;

                  
                }
                if (scope.recordTypeCode === 'military') {
                  militaryBranch = scope.qualifiers.militaryBranch;
                  militaryServiceNumber = scope.qualifiers.militaryServiceNumber;
                }
                if (scope.recordTypeCode === 'va') {
                  // const vaData = vocabData?.find((d: any) => d.vocabularyName === 'VA_Options');
                  // let vaMedicalRecords: string = '';
                  // let vaBenefitRecords: string = '';
                  // vaData.vocabularyItems.forEach((item: any) => {
                  //   if (item.code === scope.qualifiers.vaMedicalRecords[0]) {
                  //     vaMedicalRecords = item.description;
                  //   }
                  //   if (item.code === scope.qualifiers.vaBenefitRecords[0]) {
                  //     vaBenefitRecords = item.description;
                  //   }
                  // });
                  va = {
                    benefit: [_.isArray(scope.qualifiers.vaBenefitRecords) ? scope.qualifiers.vaBenefitRecords[0] : scope.qualifiers.vaBenefitRecords],
                    medical: [_.isArray(scope.qualifiers.vaMedicalRecords) ? scope.qualifiers.vaMedicalRecords[0] : scope.qualifiers.vaMedicalRecords]
                  }
                  // va = (vaMedicalRecords ? vaMedicalRecords : '')
                  //   + (vaMedicalRecords && vaBenefitRecords ? ', ' + vaBenefitRecords : vaBenefitRecords ? vaBenefitRecords : '');
                }
                if (scope.recordTypeCode === 'union') {
                  unionMembershipNumber = scope.qualifiers.unionMembershipNumber || '--';
                }
                if (scope.recordTypeCode === 'other') {
                  otherDescriptionOfRecords = scope.qualifiers.otherDescriptionOfRecords || '--';
                }
                if (scope.recordTypeCode === 'diagnostic-images') {
                    diagnosticTypeCodes = [];                  
                  _.forEach(_.split(scope.qualifiers.diagnosticTypeCodes, ','),item => {
                    diagnosticTypeCodes.push({ name: item?.trim(), code: this.getImageTypeCode(item?.trim(), this.vocabData) })
                  });
                  diagnosticFocusArea = scope.qualifiers.diagnosticFocusArea;
                  diagnosticComments = scope.qualifiers.diagnosticComments;
                }
                if (scope.recordTypeCode === 'insurance') {
                  insuranceClaimNumber = scope.qualifiers.insuranceClaimNumber;
                  insurancePolicyNumber = scope.qualifiers.insurancePolicyNumber;
                }
                if (scope.recordTypeCode === 'employment') {
                  const reqParty = this.getRequestPartyRecord(retrievalData, requestNumber);
                  if (reqParty) {
                    const matchingRole = _.find(reqParty.roles, (role: any) =>
                      role.roleCode === 'record-subject' && role.requestNumbers?.includes(requestNumber)
                    );
                  
                    employmentSSN = matchingRole?.recordSubject?.socialSecurityNumber;
                  }
                }
                if (scope.recordTypeCode === 'irs') {
                  const reqParty = this.getRequestPartyRecord(retrievalData, requestNumber);
                  if (reqParty) {
                    const matchingRole = _.find(reqParty.roles, (role: any) =>
                      role.roleCode === 'record-subject' && role.requestNumbers?.includes(requestNumber)
                    );
                  
                    irsSSN = matchingRole?.recordSubject?.socialSecurityNumber;
                  }
                }
                if (scope.recordTypeCode === 'social-security') {
                  const reqParty = this.getRequestPartyRecord(retrievalData, requestNumber);
                  if (reqParty) {
                    const matchingRole = _.find(reqParty.roles, (role: any) =>
                      role.roleCode === 'record-subject' && role.requestNumbers?.includes(requestNumber)
                    );
                    socialSecurityRes.socialSecuritySS = matchingRole?.recordSubject?.socialSecurityNumber;
                    let location = _.find(reqParty.party.locators, ['typeCode', 'location']);
                    if(location) {
                      socialSecurityRes.address = location?.location?.location?.usAddress?.lineOne ;
                      socialSecurityRes.city = location?.location?.location?.usAddress?.city ;
                      socialSecurityRes.state = {value: location?.location?.location?.usAddress?.stateCode, name: this.getStateDescription(location?.location?.location?.usAddress?.stateCode, this.vocabData)} ;
                      
                      socialSecurityRes.zip = location?.location?.location?.usAddress?.postalCode;
                    }
                  }
                }
              });

              subpoenaLanguage = request.subpoenaLanguage;
              notes = request.notes;

            } // if request number matches scope
          });

          providerInfo = {
            providerName: provider,
            providerKeys: element?.party?.keys,
            streetAddress: streetAddress,
            aptSuiteOther: aptSuiteOther,
            cityName:city,
            stateName: state,
            zipCode: zip,
            email: email,
            phone: phone,
            extn: extn,
            fax: fax,
            requestNumbers: [reqNumber],
            subject: recordSubject,
            range: recordRange,
            dateToServiceFrom: dateToServiceFrom,
            dateToServiceTo: dateToServiceto,
            selectedTypes: recordType,
            subpoenaLanguageTextarea: subpoenaLanguage,
            notesTextarea: notes,
            doctor: '',
            ambulance: {
              date: ambulanceDateOfService,
              time: ambulanceTimeOfService,
              pickupAddress: ambulancePickupStreetAddress,
              pickupCity: ambulancePickupCity,
              pickupState: ambulancePickupState,
              pickupZip: ambulancePickupZip,
              dropAddress: ambulanceDropStreetAddress,
              dropCity: ambulanceDropCity,
              dropState: ambulanceDropState,
              dropZip: ambulanceDropZip
            },
            diagnosticImages: {
              typesOfImaging: diagnosticTypeCodes,
              other: diagnosticComments,
              specificBodyPart: diagnosticFocusArea
            },
            employment: {
              employmentSS: employmentSSN
            },
            insurance: {
              claimNumber: insuranceClaimNumber,
              policyNumber: insurancePolicyNumber
            },
            irs: {
              irsSS: irsSSN
            },
            military: {
              branch: militaryBranch,
              serviceNumber: militaryServiceNumber
            },
            socialSecurity: socialSecurityRes,
            union: {
              membershipNumber: unionMembershipNumber
            },
            va: va,
            other: {
              descriptionOfRecords: otherDescriptionOfRecords
            }
          } as Record;
          if(result.records && !result.records?.records)
          result.records.records = [];
          result.records?.records.push(providerInfo);

        }); // for requests
      } // if
    }); // for roles
  }

  getRequestPartyRecord(retrievalData: any, requestNumber: number) {
    return _.find(retrievalData.parties, (party: any) =>
    _.some(party.roles, (role: any) =>
      role.roleCode === 'record-subject' && role.requestNumbers?.includes(requestNumber)
    )
  );
  }

  mapMedicalProvider(element: any, result: Order) {
    let doctor: string = element.party.displayName;

    element.roles.forEach((role: any) => {
      role.requestNumbers.forEach((requestNumber: any) => {
        let record = result.records?.records.filter((record: any) => record.requestNumbers?.includes(requestNumber));
        if (record?.length) {
          record.forEach(record => {
            record.doctor = doctor;
            record.doctorKeys = element?.party?.keys;
          });
        }
      });
    });
  }

  mapRecordSubject(element: any, result: Order) {
    element.roles.forEach((role: any) => {
      if (role.roleCode === 'record-subject') {
        let name: string = element.party.displayName;
        let alias: string = '';
        let dateOfBirth: string = '';
        let lastDigitOfSSN: string = '';
        let dateOfDeath: string = '';

        element.party.names.forEach((names: any) => {
          if (_.toLower(names.function) === 'alias') {
            alias = names.text;
          }
        });
        // dateOfBirth = this.formatDate(element?.party?.person?.dateOfBirth);
        dateOfBirth = this.dateUtilService.formatCalendarDateForDisplay(element?.party?.person?.dateOfBirth);
        lastDigitOfSSN = role.recordSubject?.socialSecurityNumber?.slice(-4);
        
        // dateOfDeath = this.formatDate(element?.party?.person?.dateOfDeath);
        dateOfDeath = this.dateUtilService.formatCalendarDateForDisplay(element?.party?.person?.dateOfDeath);

        let requestNumbers = role.requestNumbers;

        const recordSubject: Subject = {
          recordSubjectName: name,
          alias: alias,
          dateOfBirth: dateOfBirth,
          ssn: lastDigitOfSSN,
          dateOfDeath: dateOfDeath,
          requestNumbers: requestNumbers,
          keys: element.party.keys
        };
        if(result.subjects?.subjects)
          result.subjects.subjects.push(recordSubject);
      }
    });
  }

  mapDelivary(element: any, result: Order) {
    let attentionTo: string = '';
    let firmTo: string = element.party.displayName;
    let phone: string = '';
    let extension: string = '';
    let streetAddress: string = '';
    let city: string = '';
    let state: State = { name: '', value: '' };
    let zip: string = '';

    element.party.names.forEach((names: any) => {
      if (_.toLower(names.function) === 'contact-name') {
        attentionTo = names.text;
      }
    });

    element.party.locators.forEach((locator: any) => {
      if (locator.phone) {
        locator.phone.purpose === 'work' ? phone = locator.phone.number : '';
        locator.phone.purpose === 'work' ? extension = locator.phone.extension : '';
      }
      if (locator.location) {
        streetAddress = locator?.location?.location?.usAddress?.lineOne;
        city = locator?.location?.location?.usAddress?.city;
        state.value = locator?.location?.location?.usAddress?.stateCode;
        state.name = this.getStateDescription(state.value, this.vocabData);
        zip = locator?.location?.location?.usAddress?.postalCode;
      }
    });
    if (result.orderInfo) {
      result.orderInfo.orderInfoAttentionTo = attentionTo;
      result.orderInfo.orderInfoAttentionToFirmName = firmTo;
      result.orderInfo.orderInfoAttentionToFirmPhone = phone;
      result.orderInfo.orderInfoAttentionToFirmPhoneExtn = extension;
      result.orderInfo.orderInfoAttentionToFirmAddress = streetAddress;
      result.orderInfo.orderInfoAttentionToFirmCity = city;
      result.orderInfo.orderInfoAttentionToFirmState = state;
      result.orderInfo.orderInfoAttentionToFirmZip = zip;
      result.orderInfo.orderInfoAttentionToKeys = element.party.keys;
    }
  }

  mapContacts(element: any, result: Order) {
    let displayName: string = element.party.displayName;
    let email: string = '';
    let phone!: number;
    let extension!: string;
    let title: ContactTitle = {} as ContactTitle;
    let barNumber: string = '';
    let id: string = element.party.id

    element.party.locators.forEach((locator: any) => {
      if (locator.email) {
        email = locator.email.address;
      }
      if (locator.phone) {
        phone = locator.phone.number;
        extension = locator.phone.extension;
      }
    });

    element.party.names.forEach((names: any) => {
      if (_.toLower(names.function) === 'title') {
        title = {title: this.getTitleDescription(names.text, this.vocabData), value: names.text};
        // title = names.text;
      }
    });

    element.roles.forEach((role: any) => {
      barNumber = role?.attorney?.barNumber;
    })

    const newContact: Contact = {
      contactName: displayName,
      contactPhone: phone,
      contactPhoneExtn: extension,
      contactEmail: email,
      contactTitle: title,
      contactBarNo: barNumber,
      keys: element.party.keys
    };

    if(result.orderInfo && !result.orderInfo?.contacts)
      result.orderInfo.contacts = [];
      result.orderInfo?.contacts.push(newContact);
    // result.contacts.push(newContact);
  }

  mapPlaintiffCounsel(element: any, orderType: any, result: Order, caseData: any) {
    let plaintiffs: Plaintiff[] = [];
    let plantiffCounselAttorneyName: string = '';
    let plantiffCounselFirmName: string = '';
    let plantiffCounselBarNumer: string = '';
    let streetAddress: string = '';
    let city: string = '';
    let state: State = { name: '', value: '' };
    let zip: string = '';
    let phone: string = '';
    let extension: string = '';
    let fax: string = '';
    let email: string = '';
    let keys: any = element?.party?.keys;
    let recordSubjectName: RecordSubjectName[] = [];





    caseData?.partyRelationships.forEach((relation: any) => {
      if (
        relation?.sourcePartyKey ===
        keys?.find((item: any) => item?.typeCode === 'external-id')?.components['external-id']
      ) {
        
        caseData?.parties.forEach((party: any) => {
          if (party?.party?.keys?.find((item: any) => item?.typeCode === 'external-id')?.components['external-id'] === relation?.targetPartyKey) {
            let plaintiff: Plaintiff = {
              name: party?.party?.displayName,
              keys: party?.party?.keys,
              streetAddress: '',
              city: '',
              state: { name: '', value: '' },
              value: party?.party?.displayName,
              zip: ''
            };
            plaintiffs.push(plaintiff);
          }
        
      });
    }
    });
    if (element.roles.length > 0) {
      element.roles.forEach((item: any) => {
        plantiffCounselBarNumer = item?.attorney?.barNumber || '';
      });
    }
    element.party.names.forEach((names: any) => {
      if (_.toLower(names.function) === 'business-name') {
        plantiffCounselFirmName = names.text;
      }
      else if (_.toLower(names.function) === 'contact-name') {
        plantiffCounselAttorneyName = names.text;
      }
    });

    element.party.locators.forEach((locator: any) => {
      if (locator.typeCode === 'email') {
        email = locator.email?.address;
      }
      else if (locator.phone) {
        if (locator.phone.purpose === 'work') {
          phone = locator.phone?.number;
          extension = locator.phone?.extension;
        }
        if (locator.phone.purpose === 'fax') {
          fax = locator.phone?.number;
        }
      }
      else if (locator.typeCode === 'location') {
        streetAddress = locator?.location?.location?.usAddress?.lineOne;
        city = locator?.location?.location?.usAddress?.city;
        state.value = locator?.location?.location?.usAddress?.stateCode;
        state.name = this.getStateDescription(state.value, this.vocabData);
        zip = locator?.location?.location?.usAddress?.postalCode;
      }
    });

    if (orderType === 'authorization-not-provided') {
      const counselInfo: Authorization = {
        recordSubjectName: plaintiffs,
        opposingCounselAttorneyName: plantiffCounselAttorneyName,
        opposingCounselBarNumer: plantiffCounselBarNumer,
        opposingCounselFirmName: plantiffCounselFirmName,
        streetAddress: streetAddress,
        city: city,
        state: state,
        zip: zip,
        phone: phone,
        extn: extension,
        fax: fax,
        email: email,
        keys: element.party.keys
      };
      if (result.authorization && !result.authorization?.authorizations)
        result.authorization.authorizations = [];

      result.authorization?.authorizations.push(counselInfo);
      if (result.authorization)
        result.authorization.caseNumber = caseData?.caseNumber || '';

    } else if (orderType === 'subpoena-to-be-prepared') {
      const counselInfo: PlaintiffCounsel = {
        plaintiffs: plaintiffs,
        plantiffCounselAttorneyName: plantiffCounselAttorneyName,
        plantiffCounselFirmName: plantiffCounselFirmName,
        streetAddress: streetAddress,
        city: city,
        state: state,
        zip: zip,
        phone: phone,
        extn: extension,
        fax: fax,
        email: email,
        keys: element?.party?.keys,
        plantiffCounselBarNumer: plantiffCounselBarNumer
      };

      if (result.subpoena && !result.subpoena?.plaintiffCounseals)
        result.subpoena.plaintiffCounseals = [];
      result.subpoena?.plaintiffCounseals.push(counselInfo);
    }
    
  }

  mapDefendantCounsel(element: any, orderType: any, result: Order, caseData: any) {
    let defendants: Defendant[] = [];
    let defendantCounselAttorneyName: string = '';
    let defendantCounselFirmName: string = '';
    let defendantCounselBarNumer: string = '';
    let streetAddress: string = '';
    let city: string = '';
    let state: State = { name: '', value: '' };
    let zip: string = '';
    let phone: string = '';
    let extension: string = '';
    let fax: string = '';
    let email: string = '';
    let keys: any = element?.party?.keys;
    let recordSubjectName: RecordSubjectName[] = [];


     



    caseData?.partyRelationships.forEach((relation: any) => {
      if (
        relation?.sourcePartyKey ===
        keys?.find((item: any) => item?.typeCode === 'external-id')?.components['external-id']
      ) {
        
        caseData?.parties.forEach((party: any) => {
          if (party?.party?.keys?.find((item: any) => item?.typeCode === 'external-id')?.components['external-id'] === relation?.targetPartyKey) {
            let defendant: Defendant = {
              name: party?.party?.displayName,
              keys: party?.party?.keys,
              streetAddress: '',
              city: '',
              state: { name: '', value: '' },
              value: party?.party?.displayName,
              zip: ''
            };
            defendants.push(defendant);
          }
        
      });
    }
    });
    if (element.roles.length > 0) {
      element.roles.forEach((item: any) => {
        defendantCounselBarNumer = item?.attorney?.barNumber || '';
      });
    }
    element.party.names.forEach((names: any) => {
      if (_.toLower(names.function) === 'business-name') {
        defendantCounselFirmName = names.text;
      }
      else if (_.toLower(names.function) === 'contact-name') {
        defendantCounselAttorneyName = names.text;
      }
    });

    element.party.locators.forEach((locator: any) => {
      if (locator.typeCode === 'email') {
        email = locator.email?.address;
      }
      else if (locator.phone) {
        if (locator.phone.purpose === 'work') {
          phone = locator.phone?.number;
          extension = locator.phone?.extension;
        }
        if (locator.phone.purpose === 'fax') {
          fax = locator.phone?.number;
        }
      }
      else if (locator.typeCode === 'location') {
        streetAddress = locator?.location?.location?.usAddress?.lineOne;
        city = locator?.location?.location?.usAddress?.city;
        state.value = locator?.location?.location?.usAddress?.stateCode;
        state.name = this.getStateDescription(state.value, this.vocabData);
        zip = locator?.location?.location?.usAddress?.postalCode;
      }
    });

    if (orderType === 'authorization-not-provided') {
      const counselInfo: Authorization = {
        recordSubjectName: recordSubjectName,
        opposingCounselAttorneyName: defendantCounselAttorneyName,
        opposingCounselBarNumer: defendantCounselBarNumer,
        opposingCounselFirmName: defendantCounselFirmName,
        streetAddress: streetAddress,
        city: city,
        state: state,
        zip: zip,
        phone: phone,
        extn: extension,
        fax: fax,
        email: email,
        keys: element.party.keys
      };
      if (result.authorization && !result.authorization?.authorizations)
        result.authorization.authorizations = [];

      result.authorization?.authorizations.push(counselInfo);
      if (result.authorization)
        result.authorization.caseNumber = caseData?.caseNumber || '';

    } else if (orderType === 'subpoena-to-be-prepared') {
      const counselInfo: DefendantCounsel = {
        defendants: defendants,
        defendantCounselAttorneyName: defendantCounselAttorneyName,
        defendantCounselFirmName: defendantCounselFirmName,
        streetAddress: streetAddress,
        city: city,
        state: state,
        zip: zip,
        phone: phone,
        extn: extension,
        fax: fax,
        email: email,
        keys: element?.party?.keys,
        defendantCounselBarNumer: defendantCounselBarNumer
      };

      if (result.subpoena && !result.subpoena?.defendantCounseals)
        result.subpoena.defendantCounseals = [];
      result.subpoena?.defendantCounseals.push(counselInfo);
    }
  }

  

  mapCourt(element: any, result: Order, caseData: any) {
    let judge: string = '';
    let court: string = element.party.displayName;
    let streetAddress: string = '';
    let city: string = '';
    let state: State = {name: '', value: ''};
    let zip: string = '';
    let jurisdictionCounty: string = '';

    element.roles.forEach((role: any) => {
      judge = role.court?.judgeName;
      jurisdictionCounty = role.court?.jurisdiction;
    });

    element.party.locators.forEach((locator: any) => {
      if (locator.typeCode === 'location') {
        streetAddress = locator?.location?.location?.usAddress?.lineOne ;
        city = locator?.location?.location?.usAddress?.city ;
        state.value = locator?.location?.location?.usAddress?.stateCode ;
        state.name = this.getStateDescription(state.value, this.vocabData);
        zip = locator?.location?.location?.usAddress?.postalCode;
      }
    });

      result.subpoena = {...result.subpoena,
        court : court,
        judge : judge,
        courtKeys: element.party.keys,
        courtStreetAddress: streetAddress,
        jurisdictionCounty: jurisdictionCounty,
        state: state,
        cityName: city,
        zipCode: zip,
        // caseNumber: caseData?.qualifiers?.caseNumber
      } as Subpoena
    
    
  }

  mapAttachments(attachmentsData: any, result: Order) {
    let storageId: string = '';
    let storageProviderId: string = '';
    let contentType: string = '';
    let fileName: string = '';
    let displayName: string = '';
    let description: string = '';
    let sizeBytes!: number;
    let sizeText: string = '';

    const orderDocuments = _.find(attachmentsData, ['name','Order Documents']);
    if (orderDocuments && orderDocuments.documentItems) {
      orderDocuments.documentItems?.forEach((element: any) => {
        if (element) {
          storageId = element.storageId;
          storageProviderId = element.storageProviderId;
          contentType = element.contentType;
          fileName = element.fileName;
          description = element.description;
          sizeBytes = this.convertToBytes(element.size);
          sizeText = element.size;
          displayName = element.displayName;
        }

        const attachmentDetail: Attachment = {
          filename: fileName,
          description: description,
          label: description,
          fileId: Date.now(),
          size: sizeBytes,
          sizeText: sizeText,
          storageId: storageId,
          storageProviderId: storageProviderId,
          type: contentType,
          deleteDisabled: true
        };
        if (result.orderInfo && !result.orderInfo.orderInfoAttachmentFile)
          result.orderInfo.orderInfoAttachmentFile = [];
        result.orderInfo?.orderInfoAttachmentFile.push(attachmentDetail);
      });
    }
    
  }

  convertToBytes(sizeStr: string): number {
    const units: { [key: string]: number } = {
      B: 1,
      KB: 1024,
      MB: 1024 ** 2,
      GB: 1024 ** 3,
      TB: 1024 ** 4,
    };

    const match = sizeStr.trim().match(/^([\d.]+)\s*(B|KB|MB|GB|TB)$/i);

    if (!match) {
      return 0;
    }

    const value = parseFloat(match[1]);
    const unit = match[2].toUpperCase();

    return Math.round(value * (units[unit] || 1));
  }
isExternalIdTrue(partyFlag: Boolean, keys: any[]): Boolean {
    _.forEach(keys, key => {
      if (key?.typeCode === 'external-id' && key?.isUnique) {
        partyFlag = true;
        // do nothing, just checking
      }
    });
    return partyFlag;
  }
  
}

